import React, { useState } from 'react';
import { Truck, Loader2 } from 'lucide-react';
import { checkShippingServiceability } from '../api/shipping';

interface PincodeCheckerProps {
  defaultPincode?: string;
}

const PincodeChecker: React.FC<PincodeCheckerProps> = ({ defaultPincode = '' }) => {
  const [pincode, setPincode] = useState(defaultPincode);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ etd?: string; error?: string } | null>(null);

  const checkPincode = async () => {
    if (!pincode || pincode.length < 6) {
      setResult({ error: 'Please enter a valid 6-digit pincode.' });
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      // Dummy pickup code is 560001
      const res = await checkShippingServiceability('560001', pincode);
      if (res.data?.data?.available_courier_companies?.length > 0) {
        setResult({ etd: res.data.data.available_courier_companies[0].etd });
      } else {
        setResult({ error: 'Delivery not available for this pincode.' });
      }
    } catch (err: any) {
      setResult({ error: err.message || 'Failed to check pincode.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
      <div className="flex items-center text-gray-900 font-semibold mb-3">
        <Truck className="w-5 h-5 mr-2 text-primary-600" /> Check Estimated Delivery
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          maxLength={6}
          placeholder="Enter Pincode"
          value={pincode}
          onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
        />
        <button
          onClick={checkPincode}
          disabled={loading || pincode.length < 6}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
        </button>
      </div>
      {result?.etd && (
        <div className="mt-3 text-sm text-green-700 font-medium">
          Estimated Delivery: {result.etd}
        </div>
      )}
      {result?.error && (
        <div className="mt-3 text-sm text-red-600">
          {result.error}
        </div>
      )}
    </div>
  );
};

export default PincodeChecker;
